import { FC, useState } from "react";
import dayjs from "dayjs";
import { condensedReadableDate } from "../hooks/DateTime";
import defaultHeaderImage from "../assets/header.png";
import { EventData } from "../types/firebase";
import { truncateText } from "../utils/tools";
import Pill from "./Pill";
import { getEventTypeLabel } from "../constants/eventTypes";
import { Link } from "react-router-dom";
import { Blurhash } from "react-blurhash";
import { getFirestore, doc, deleteDoc } from 'firebase/firestore';
import { useFirebase } from "../contexts/FirebaseContext";
import Button from "./Buttons/Button";

interface FeaturedCardProps {
  event: EventData;
  hideHeaderImage?: boolean;
  location?: string;
  userCanEdit?: boolean;
}

const FeaturedCard: FC<FeaturedCardProps> = ({ 
  event, 
  hideHeaderImage = false,
  location = 'home',
  userCanEdit = false
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { app } = useFirebase();
  const headerImage = event.header_image || defaultHeaderImage;

  const handleDelete = async () => {
    try {
      const db = getFirestore(app);
      await deleteDoc(doc(db, 'events', event.id));
      setShowDeleteModal(false);
      // You might want to add a success notification here
      window.location.reload(); // Refresh to show updated list
    } catch (error) {
      console.error('Error deleting event:', error);
      // You might want to add an error notification here
    }
  };

  return (
    <>
      <Link to={`/events/view?id=${event.id}&location=${location}`}>
        <div className="h-[32rem] bg-muted-lightGrey hover:bg-white transition-background duration-300 rounded-lg border bg-muted-grey border-muted-grey hover:shadow-[10px_10px_0_-2px_theme(colors.muted.grey)] overflow-hidden flex flex-col gap-4 max-w-4xl transition-shadow">
          {/* Header Image */}
          {!hideHeaderImage && (
            <div className="w-full h-48 flex-shrink-0 relative">
              {event.header_image_blur_hash && (
                <Blurhash
                  hash={event.header_image_blur_hash}
                  width="100%"
                  height="100%"
                  resolutionX={32}
                  resolutionY={32}
                  punch={1}
                  className={`
                    absolute inset-0 w-full h-full
                    ${imageLoaded ? 'animate-fade-out' : ''}
                  `}
                />
              )}
              <img
                src={headerImage}
                alt={event.event_name}
                loading="lazy"
                className={`
                  absolute inset-0
                  w-full h-full 
                  object-cover 
                  ${imageLoaded ? 'animate-fade-in' : 'opacity-0'}
                `}
                onLoad={() => setTimeout(() => setImageLoaded(true), 100)}
              />
            </div>
          )}

          {/* Content section */}
          <div className="flex-1 flex flex-col p-6 overflow-hidden">
            <div className="overflow-hidden">
              <h4 className="uppercase text-muted-grey font-bold text-sm">{event.provider_name}</h4>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                {event.event_name}
              </h3>
              {((event.event_country && event.event_region) ||
                (event.providerDetails?.provider_country &&
                  event.providerDetails?.provider_region)) && (
                <span>
                  {event.event_country || event.providerDetails?.provider_country},{" "}
                  {event.event_region || event.providerDetails?.provider_region}
                </span>
              )}
              <h4 className="text-sm text-gray-600 mb-2">
                {dayjs.isDayjs(event.date) ? condensedReadableDate(event.date) : event.date} {event.start_time}
              </h4>
              <p className="text-gray-700 mb-4 text-xs line-clamp-3">
                {truncateText(event.event_description, 100)}
              </p>
            </div>

            {/* Add duplicate link if user has permission */}
            {userCanEdit && (
              <div className="mt-2">
                <Link 
                  to={`/submit?duplicate=${event.id}`}
                  className="text-sm underline text-links-default hover:text-links-hover"
                  onClick={(e) => e.stopPropagation()}
                >
                  ➕Duplicate
                </Link>
              </div>
            )}
            {userCanEdit && (
              <div className="mt-2">
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDeleteModal(true);
                  }}
                  className="text-sm underline text-links-default hover:text-links-hover"
                >
                  🗑️ Delete
                </button>
              </div>
            )}
            {
              userCanEdit && (
                <div className="mt-2">
                  <Link 
                    to={`/submit?edit=${event.id}`}
                    className="text-sm underline text-links-default hover:text-links-hover"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ✏️Edit
                  </Link>
                </div>
              )
            }

            <div className="mt-auto flex items-center justify-end">
              {/* for now just showing one event type as multiple break the layout */}
              {event.event_types && (
                <Pill
                  text={getEventTypeLabel(event.event_types[0]) || 'Unknown Type'}
                  size="small"
                  colorClass="bg-muted-lavender"
                />
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-xl font-semibold mb-4">Delete Confirmation</h3>
            <p className="mb-6">Are you sure you would like to delete {event.event_name}?</p>
            <div className="flex justify-end gap-4">
              <Button
                color="moss text-black"
                onClick={() => setShowDeleteModal(false)}
              >
                No
              </Button>
              <Button
                onClick={handleDelete}
              >
                Yes
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeaturedCard;
